/* eslint-disable react/jsx-props-no-spreading,@typescript-eslint/ban-types */
import { createComponent, ReactWebComponent } from '@lit-labs/react';
import React, { useState } from 'react';
import createBasicWrapper from './createBasicWrapper';

interface Constructor<T> {
  new (): T;
}
type EventName<T extends Event = Event> = string & {
  __event_type: T;
};
type EventNames = Record<string, EventName | string>;

const isServer = typeof window === 'undefined';

export const createWrapper = <E extends HTMLElement = HTMLElement, V extends EventNames = {}>(
  tagName: string,
  events?: V,
) => {
  const BasicWrappedComponent = createBasicWrapper(tagName);
  BasicWrappedComponent.displayName = `basicWrapped(${tagName})`;

  const loader = async () => {
    if (isServer) return BasicWrappedComponent;

    await customElements.whenDefined(tagName);

    return createComponent({
      react: React,
      tagName,
      events,
      displayName: `litReactWrapped(${tagName})`,
      elementClass: customElements.get(tagName) as Constructor<E>,
    });
  };

  let LoadedElement: any;

  const whenLoaded = loader().then((element) => {
    LoadedElement = element;
  });

  const ForwardedComponent = React.forwardRef(({ whenDefined, ...props }: any, forwardedRef) => {
    const [isLoading, setIsLoading] = useState(!LoadedElement);

    React.useEffect(() => {
      let cancelled = false;
      whenLoaded.then((result) => {
        if (cancelled) return;
        whenDefined?.(LoadedElement);
        setIsLoading(Boolean(result));
      });
      return () => {
        cancelled = true;
      };
    }, []);

    if (isLoading) return <BasicWrappedComponent ref={forwardedRef} {...props} />;

    return <LoadedElement ref={forwardedRef} {...props} />;
  });

  ForwardedComponent.displayName = `wrapped(${tagName})`;

  return ForwardedComponent as unknown as ReactWebComponent<
    E,
    V & { whenDefined?: (element: typeof LoadedElement) => void }
  >;
};

export default createWrapper;
